import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  if (a === 0 && b === 0) return 1;
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a === 0 ? 1 : a;
};

const simplifyFraction = (num, den) => {
  const g = gcd(num, den);
  return {
    numerator: num / g,
    denominator: den / g,
  };
};

const fractionToLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";
  if (denominator === 1) return `${numerator}`;
  return `\\dfrac{${numerator}}{${denominator}}`;
};

const improperToMixedLatex = ({ numerator, denominator }) => {
  if (!denominator || denominator === 0) return "";

  const sign = numerator < 0 ? -1 : 1;
  const absNum = Math.abs(numerator);

  if (absNum < denominator) {
    return fractionToLatex({ numerator, denominator });
  }

  const whole = Math.floor(absNum / denominator);
  const rem = absNum % denominator;

  if (rem === 0) {
    return `${sign * whole}`;
  }

  const fracPart = `\\dfrac{${rem}}{${denominator}}`;
  if (sign === -1) return `-${whole}\\;${fracPart}`;
  return `${whole}\\;${fracPart}`;
};

const isValidFractionObject = (f) =>
  f &&
  typeof f === "object" &&
  f.numerator !== undefined &&
  f.denominator !== undefined;

const areFractionsEqual = (a, b) => {
  if (!isValidFractionObject(a) || !isValidFractionObject(b)) return false;

  const an = Number(a.numerator);
  const ad = Number(a.denominator);
  const bn = Number(b.numerator);
  const bd = Number(b.denominator);

  if (!isFinite(an) || !isFinite(ad) || !isFinite(bn) || !isFinite(bd)) {
    return false;
  }
  if (ad === 0 || bd === 0) return false;

  return an * bd === bn * ad;
};

const stripPercent = (s) => s.replace(/\s*%$/, "").trim();

/** Parsowanie tekstu na ułamek zwykły / mieszany (ignorujemy ewentualny % na końcu).
 *  Jeśli jest kropka/przecinek – uznajemy, że to zapis dziesiętny i zwracamy null. */
const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  let trimmed = input.trim();
  if (!trimmed) return null;

  trimmed = stripPercent(trimmed);
  if (!trimmed) return null;

  // jeśli jest . lub , traktujemy to jako liczbę dziesiętną, nie ułamek zwykły
  if (/[.,]/.test(trimmed)) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    // zapis mieszany: "2 3/5"
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else {
      return null;
    }
  }

  return simplifyFraction(num, den);
};

/** Parsowanie zapisu dziesiętnego (kropka lub przecinek, opcjonalnie z %) */
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  let trimmed = input.trim();
  if (!trimmed) return null;

  trimmed = stripPercent(trimmed);
  if (!trimmed) return null;

  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

/* ======================= GENERATOR ======================= */
/**
 * Zadanie: Jaki procent stanowi jedna liczba drugiej?
 *
 * difficulty: "easy"  – wynik procentowy jest liczbą całkowitą,
 *             "hard"  – wynik może być niecałkowity (np. 13,5%).
 */
export function generateTask(config = {}) {
  const { difficulty = "easy" } = config;

  let percentFraction; // szukany procent jako ułamek
  if (difficulty === "hard") {
    // kilka przykładowych "niecałkowitych" procentów:
    // 13.5%, 12.5%, 22.5%, 17.5%, 37.5%
    const hardPercents = [
      { numerator: 27, denominator: 2 },   // 13.5
      { numerator: 25, denominator: 2 },   // 12.5
      { numerator: 45, denominator: 2 },   // 22.5
      { numerator: 35, denominator: 2 },   // 17.5
      { numerator: 75, denominator: 2 },   // 37.5
      { numerator: 135, denominator: 10 }, // 13.5
    ];
    percentFraction =
      hardPercents[randInt(0, hardPercents.length - 1)];
  } else {
    // ładne, całkowite procenty
    const easyPercents = [10, 12, 15, 20, 25, 30, 40, 50, 60, 75];
    const p = easyPercents[randInt(0, easyPercents.length - 1)];
    percentFraction = { numerator: p, denominator: 1 };
  }

  const num = percentFraction.numerator;
  const den = percentFraction.denominator;

  // Wybieramy liczbę bazową tak, żeby "część" była całkowita:
  // part = (num/den)/100 * base = num*base / (100*den)
  const baseStep = (100 * den) / gcd(100 * den, num);
  const k = difficulty === "hard" ? randInt(1, 3) : randInt(2, 6);
  const base = baseStep * k;

  const part = (num * base) / (100 * den); // gwarantujemy całkowitość

  return {
    type: "PercentWhatPartTask",
    data: {
      difficulty,
      part,            // np. 18
      base,            // np. 120
      percentFraction, // np. 15/1 albo 27/2
    },
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const { part, base } = data;

  return (
    <p>
      Jaki procent liczby{" "}
      <InlineMath math={`${base}`} /> stanowi liczba{" "}
      <InlineMath math={`${part}`} />?
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      // nie chcemy etykiety typu "p = ..."
      label=""
      // w podglądzie i obok inputu ma się pojawiać znak %
      suffixLatex="\%"
      placeholder="np. 20, 13 1/2, 27/2"
    />
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.percentFraction;
  const expectedValue = expected.numerator / expected.denominator;

  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return {
      isCorrect: false,
      expected,
      actual: null,
      comment: null,
    };
  }

  const raw = String(userAnswer.raw).trim();

  // 1) zapis dziesiętny (obsługuje też np. "75%")
  const decimalVal = parseDecimalFromString(raw);
  if (decimalVal !== null) {
    const approxNum = Math.round(decimalVal * 100);
    const approxDen = 100;
    const approxFrac = simplifyFraction(approxNum, approxDen);

    const sameFraction = areFractionsEqual(expected, approxFrac);
    const closeEnough = Math.abs(decimalVal - expectedValue) <= 0.005;

    const isCorrect = sameFraction || closeEnough;

    return {
      isCorrect,
      expected,
      actual: approxFrac,
      comment: null,
    };
  }

  // 2) ułamek zwykły / mieszany – interpretujemy jako LICZBĘ procentów
  const parsedFrac = parseFractionFromString(raw);
  if (parsedFrac && areFractionsEqual(expected, parsedFrac)) {
    return {
      isCorrect: true,
      expected,
      actual: parsedFrac,
      comment: null,
    };
  }

  return {
    isCorrect: false,
    expected,
    actual: null,
    comment: null,
  };
}

/* ======================= ANSWER & USER ANSWER ======================= */

export function renderAnswer({ data }) {
  const { percentFraction } = data;
  const coreLatex = improperToMixedLatex(percentFraction); // np. 75 lub 13 1/2

  const latex = `${coreLatex}\\%`;

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={latex} />
    </div>
  );
}

function formatPercentRawToLatex(raw) {
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";

  // obcinamy ewentualny % na końcu
  const noPercent = trimmed.replace(/\s*%\s*$/, "").trim();

  // zapis mieszany: 13 1/2
  const mixedMatch = noPercent.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixedMatch) {
    const whole = mixedMatch[1];
    const num = mixedMatch[2];
    const den = mixedMatch[3];
    return `${whole}\\;\\dfrac{${num}}{${den}}\\%`;
  }

  // zwykły ułamek: 27/2, 54/4 itd.
  const fracMatch = noPercent.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (fracMatch) {
    const num = fracMatch[1];
    const den = fracMatch[2];
    return `\\dfrac{${num}}{${den}}\\%`;
  }

  // liczba całkowita lub dziesiętna (kropka albo przecinek)
  const normalized = noPercent.replace(",", ".");
  if (!isNaN(Number(normalized))) {
    // zostawiamy zapis tak jak wpisał uczeń
    return `${noPercent}\\%`;
  }

  // awaryjnie – pokazujemy prawie surowo
  const escaped = noPercent.replace(/%/g, "\\%");
  return `${escaped}\\%`;
}


export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.raw ||
    String(userAnswer.raw).trim() === ""
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latex = formatPercentRawToLatex(userAnswer.raw);

  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={latex} />
    </div>
  );
}


/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const { part, base, percentFraction } = data;

  const pLatex = improperToMixedLatex(percentFraction);

  const step1 = `\\dfrac{p}{100} \\cdot ${base} = ${part}`;
  const step2 = `p = \\dfrac{${part}\\cdot 100}{${base}}`;
  const step3 = `p = ${pLatex}\\%`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Zakładamy, że szukany procent to{" "}
        <InlineMath math={"p"} /> i zapisujemy równanie:
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2:</strong> Dzielimy obie strony równania przez{" "}
        <InlineMath math={`\\dfrac{${base}}{100}`} /> i wyznaczamy{" "}
        <InlineMath math={"p"} />.
      </p>
      <BlockMath math={step2} />

      <p>
        <strong>Krok 3:</strong> Uprość otrzymany ułamek i odczytaj wartość
        procentową.
      </p>
      <BlockMath math={step3} />
    </div>
  );
}
